import { assertUnreachable } from "src/utils/typeUtils";

type EqualityOperator = "lt" | "le" | "eq" | "ne" | "gt" | "ge";
type InOperator = "is_in" | "not_is_in";
type NoneOperator = "is_none" | "is_not_none";
type ContainsOperator = "contains" | "does_not_contain";
type RegexOperator = "matches_regex";
type AnyOperator = "is_any";
type StringOperator = "starts_with" | "ends_with" | "str_contains" | "like";

export type Operator =
  | EqualityOperator
  | InOperator
  | NoneOperator
  | ContainsOperator
  | RegexOperator
  | AnyOperator
  | StringOperator;
export type OperatorDefinition = {
  description: string;
  code: string;
  displayCode?: React.ReactNode;
};

export const EQUALITY_OPERATORS: Record<EqualityOperator, OperatorDefinition> =
  {
    lt: { description: "Less than", code: "<" },
    le: { description: "Less than or equal to", code: "<=" },
    eq: { description: "Equal to", code: "==" },
    ne: { description: "Not equal to", code: "!=" },
    gt: { description: "Greater than", code: ">" },
    ge: { description: "Greater than or equal to", code: ">=" },
  } as const;

export const OPERATORS: Record<
  | EqualityOperator
  | InOperator
  | NoneOperator
  | ContainsOperator
  | RegexOperator,
  OperatorDefinition
> = {
  ...EQUALITY_OPERATORS,
  is_in: { description: "Is in", code: "is in" },
  not_is_in: { description: "Is not in", code: "is not in" },
  is_none: { description: "Is None", code: "is None" },
  is_not_none: { description: "Is not None", code: "is not None" },
  contains: { description: "Contains", code: "contains" },
  does_not_contain: {
    description: "Does not contain",
    code: "does not contain",
    displayCode: <span className="line-through">contains</span>,
  },
  matches_regex: { description: "Matches regex", code: "matches regex" },
} as const;

export const OPERATORS_WITH_IS_ANY: Record<
  | EqualityOperator
  | InOperator
  | NoneOperator
  | ContainsOperator
  | RegexOperator
  | AnyOperator,
  OperatorDefinition
> = {
  ...OPERATORS,
  is_any: { description: "Is any", code: "is any" },
} as const;

const isOperatorInRecord = (
  op: Operator,
): op is keyof typeof OPERATORS_WITH_IS_ANY =>
  Object.keys(OPERATORS_WITH_IS_ANY).includes(op);

/**
 * Returns default definition for operators
 */
export const getOperatorDefinition = (
  operator: Operator,
): OperatorDefinition => {
  switch (operator) {
    case "starts_with":
      return { description: "Starts with", code: "starts with" };
    case "ends_with":
      return { description: "Ends with", code: "ends with" };
    case "str_contains":
      return { description: "Contains", code: "contains" };
    case "like":
      return { description: "Like", code: "like" };
    default:
      if (isOperatorInRecord(operator)) {
        return OPERATORS_WITH_IS_ANY[operator];
      }

      assertUnreachable(operator);
      return OPERATORS_WITH_IS_ANY[
        operator as keyof typeof OPERATORS_WITH_IS_ANY
      ];
  }
};
